import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectActiveFocusUnit } from '../redux/reducers/focusSlice';
import { useDefaultUnit } from './useDefaultUnit';
import { useStudentSkillStates } from './useStudentSkillStates';
import { isUnitComplete } from '../businessLogicService/skillCompletionUtils';
import { Unit } from '../models/Unit';

/**
 * Resolves the student's current/default unit, focus-aware.
 *
 * Priority: the teacher-set focus unit wins while it is still incomplete and
 * part of `units`; otherwise fall back to the progress default (first incomplete
 * unit) from useDefaultUnit. Block mode plays no part in this decision. The focus
 * comes from focusSlice, populated on the home route by useStudentActiveFocus.
 */
export const useResolvedUnit = (units?: Unit[]): Unit | null => {
  const defaultUnit = useDefaultUnit(units);
  const activeFocusUnit = useAppSelector(selectActiveFocusUnit);
  const { studentSkillStates } = useStudentSkillStates({ filterByTargetGroup: true, includeExtensions: false });

  return useMemo(() => {
    const focusInGroup = activeFocusUnit ? units?.find(unit => unit.id === activeFocusUnit.id) ?? null : null;
    if (focusInGroup && !isUnitComplete(studentSkillStates, focusInGroup.id)) {
      return focusInGroup;
    }
    return defaultUnit;
  }, [activeFocusUnit, units, studentSkillStates, defaultUnit]);
};
